<script lang="ts" setup>
import { computed } from 'vue'

import Markdown from '@satch-ui/components/markdown/Markdown.vue'
import ContentBuilderContainer from '@storefront-satch/components/molecules/content-builder/ContentBuilderContainer.vue'
import ProductViewPropertiesAttributes from '@storefront-satch/components/organisms/product-view-properties/ProductViewPropertiesAttributes.vue'
import { skusWithBluesign } from '@storefront-satch/components/organisms/product-view-properties/ProductViewPropertiesData.ts'
import ProductViewPropertiesItem from '@storefront-satch/components/organisms/product-view-properties/ProductViewPropertiesItem.vue'
import type { ProductViewPropertiesProps } from '@storefront-satch/components/organisms/product-view-properties/types.ts'
import config from '@storefront-satch/config'
import { t } from '@storefront-satch/lib/translations'

const props = defineProps<ProductViewPropertiesProps>()

const formatDimension = (value?: string) => {
  return typeof value === 'string' ? value.replace('.0', '') : value
}

type ContentManual = {
  [key: string]: string
}

const contentManual = computed(() => {
  return {
    bottle: props.content.manualBottleContent,
    sport_drinking_bottle: props.content.manualBottleSportContent,
  } as ContentManual
})

const details = computed(() => {
  const details = []
  let label = ''
  let value = ''

  if (props.sku) {
    label = t('product.property.articleNo')
    value = props.sku
    details.push({ label, value })
  }

  if (props.colors) {
    label = t('product.property.color')
    value = props.colors?.join(', ')
    details.push({ label, value })
  }

  if (props.width && props.height && props.depth) {
    label = t('product.property.size')
    value = `${formatDimension(props.width)} x ${formatDimension(props.height)} x ${formatDimension(props.depth)} ${config.units.lengthShort} (${t('product.property.sizeInfo')})`
    details.push({ label, value })
  }

  if (props.weight) {
    label = t('product.property.weight')
    value = `${formatDimension(props.weight)} ${config.units.weightShort}`
    details.push({ label, value })
  }

  if (props.volume) {
    label = t('product.property.volume')
    value = `${formatDimension(props.volume)} ${t('product.property.liter')}`
    details.push({ label, value })
  }

  return details
})
</script>
<template>
  <ContentBuilderContainer
    id="Product Properties"
    class="align-start relative flex flex-wrap"
    data-label="Product Properties"
    data-title="Product Properties"
  >
    <ProductViewPropertiesItem>
      <template #headline>
        {{ t('product.property.details') }}
      </template>
      <template #content>
        <ProductViewPropertiesAttributes
          v-for="item in details"
          :key="item.label"
          :label="item.label"
        >
          {{ item.value }}
        </ProductViewPropertiesAttributes>
        <template v-if="props.modelKey">
          <div
            v-if="contentManual[props.modelKey]"
            class="mt-2"
          >
            <Markdown :content="contentManual[props.modelKey]" />
          </div>
        </template>
      </template>
    </ProductViewPropertiesItem>

    <ProductViewPropertiesItem v-if="props.featureDescription">
      <template #headline>
        {{ t('product.property.features') }}
      </template>
      <template #content>
        <Markdown :content="props.featureDescription" />
      </template>
    </ProductViewPropertiesItem>

    <ProductViewPropertiesItem v-if="props.materialDescription">
      <template #headline>
        {{ t('product.property.material') }}
      </template>
      <template #content>
        <Markdown :content="props.materialDescription" />
      </template>
    </ProductViewPropertiesItem>

    <ProductViewPropertiesItem v-if="props.instructionsForUse">
      <template #headline>
        {{ t('product.property.safetyAndConformity') }}
      </template>
      <template #content>
        <Markdown
          v-if="props.instructionsForUse"
          :content="props.instructionsForUse"
        />
      </template>
    </ProductViewPropertiesItem>

    <ProductViewPropertiesItem
      v-if="
        !props.modelKey?.includes('bottle') &&
        !props.modelKey?.includes('lunchbox')
      "
    >
      <template #headline>
        {{ t('product.property.responsibility') }}
      </template>
      <template #content>
        <Markdown
          v-if="
            props.sku &&
            skusWithBluesign.includes(props.sku) &&
            props.content.bluesignContent
          "
          :content="props.content.bluesignContent"
        />
        <Markdown
          v-else-if="
            props.modelKey?.includes('cross') && props.content.crossContent
          "
          :content="props.content.crossContent"
        />
        <Markdown
          v-else-if="
            props.modelKey?.includes('fly') && props.content.flyContent
          "
          :content="props.content.flyContent"
        />
        <Markdown
          v-else-if="props.content.responsabilityContent"
          :content="props.content.responsabilityContent"
        />
      </template>
    </ProductViewPropertiesItem>
  </ContentBuilderContainer>
</template>
